/**
 * Webhook_Schema.ts
 * TypeScript types for Catalog Webhooks API  (sk-catalog-api /api/v1/webhooks)
 * Body fields match CreateWebhookBody / PatchWebhookBody (camelCase).
 */

export type WebhookStatus = 'ACTIVE' | 'SUSPENDED';

// Catalog event types a webhook can subscribe to (sk-catalog-api
// SUBSCRIBABLE_EVENT_TYPES — must match exactly).
export type WebhookEvent =
  | '*'
  | 'ITEM_UPSERT'
  | 'ITEM_DEACTIVATE'
  | 'BUNDLE_INVALIDATE'
  | 'BUNDLE_ATTENTION'
  | 'COMPONENT_PRICE_CHANGED'
  | 'MEDIA_READY'
  | 'MEDIA_FAILED'
  | 'SCHEDULED_TRANSITION'
  | 'CATEGORY_UPSERT'
  | 'CATEGORY_DELETE'
  | 'TAG_UPSERT'
  | 'TAG_DELETE'
  | 'COLLECTION_UPSERT'
  | 'COLLECTION_DELETE'
  | 'LOCATION_UPSERT'
  | 'LOCATION_DELETE'
  | 'ITEM_TYPE_UPSERT'
  | 'FIELD_UPSERT'
  | 'FIELD_DELETE';

// Subscribable events with human-friendly labels for the UI.
export const WEBHOOK_EVENTS: { value: WebhookEvent; label: string }[] = [
  { value: '*', label: '★ All events (everything that changes)' },
  { value: 'ITEM_UPSERT', label: 'Item created / updated' },
  { value: 'ITEM_DEACTIVATE', label: 'Item deactivated / deleted' },
  { value: 'BUNDLE_INVALIDATE', label: 'Bundle invalidated' },
  { value: 'BUNDLE_ATTENTION', label: 'Bundle needs attention' },
  { value: 'COMPONENT_PRICE_CHANGED', label: 'Component price changed' },
  { value: 'MEDIA_READY', label: 'Media ready' },
  { value: 'MEDIA_FAILED', label: 'Media failed' },
  { value: 'SCHEDULED_TRANSITION', label: 'Scheduled transition' },
  { value: 'CATEGORY_UPSERT', label: 'Category created / updated' },
  { value: 'CATEGORY_DELETE', label: 'Category deleted' },
  { value: 'TAG_UPSERT', label: 'Tag created / assigned' },
  { value: 'TAG_DELETE', label: 'Tag deleted' },
  { value: 'COLLECTION_UPSERT', label: 'Collection created / updated' },
  { value: 'COLLECTION_DELETE', label: 'Collection deleted' },
  { value: 'LOCATION_UPSERT', label: 'Location created / updated' },
  { value: 'LOCATION_DELETE', label: 'Location deleted' },
  { value: 'ITEM_TYPE_UPSERT', label: 'Item type created / updated' },
  { value: 'FIELD_UPSERT', label: 'Field added' },
  { value: 'FIELD_DELETE', label: 'Field deleted' },
];

/* ─────────────────────────────────────────
   Request types
───────────────────────────────────────── */

export interface CreateWebhookPayload {
  url:         string;
  eventTypes:  (WebhookEvent | string)[];
  description?: string;
}

export interface UpdateWebhookPayload {
  url?:         string;
  eventTypes?:  (WebhookEvent | string)[];
  status?:      WebhookStatus;
  description?: string;
}

/* ─────────────────────────────────────────
   Response types
───────────────────────────────────────── */

export interface Webhook {
  id:           string;
  url:          string;
  eventTypes:   string[];
  status:       WebhookStatus;
  description?: string | null;
  secret?:      string;
  createdAt?:   string;
  updatedAt?:   string;
}

export type WebhookResponse = Webhook;

export interface WebhooksResponse {
  items: Webhook[];
}
